import { AlertTriangle, Check, Loader2, Lock, Shield, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaAccordion, ColocaAccordionItem } from "@/shared/components/layout/ColocaAccordion";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { toastApiError } from "@/shared/utils/toast";

import { ACCESS_CATALOG } from "../../constants/accessCatalog.constants";
import type { AccessRole } from "../../types/access.types";
import { gatherStatements } from "../../utils/denyFirst";
import { roleColor } from "../../utils/roleColor";
import { resolveRoleGroup, roleGroupId } from "../../utils/roleModuleGroup";
import { AccessSavedIndicator, type SaveStatus } from "./AccessSavedIndicator";
import { EffectiveMatrix } from "./EffectiveMatrix";
import { PrincipalAvatar } from "./PrincipalAvatar";

/** One accordion group: a catalog module, or one of the non-module buckets. */
interface RoleBucket {
  id: string;
  label: string;
  icon?: LucideIcon;
  roles: AccessRole[];
}

/** A bucket plus the tally of what is ticked inside it right now. */
interface RoleGroupView extends RoleBucket {
  selectedCount: number;
}

/**
 * The groups to expand when the drawer opens: those the principal ALREADY holds a role in.
 *
 * Read from the server's assignment list, and only at seed time. The drawer's live selection is what the
 * operator is editing, and `assignedRoleIds` itself moves after every successful save — a default tracking
 * either one would let a panel collapse under the very click that emptied it, and re-open it if that save
 * then failed.
 */
function groupsHoldingRoles(
  assignedRoleIds: readonly string[],
  roles: readonly AccessRole[],
): Record<string, boolean> {
  const byId = new Map(roles.map((role) => [role.id, role]));
  const open: Record<string, boolean> = {};
  for (const roleId of assignedRoleIds) {
    const role = byId.get(roleId);
    if (role) open[roleGroupId(resolveRoleGroup(role))] = true;
  }
  return open;
}

export interface AssignRolesPrincipal {
  id: string;
  name: string;
  kind: "person" | "key";
  /** Email (person) or masked prefix (key). */
  subtitle?: string;
}

export interface AssignRolesDrawerProps {
  principal: AssignRolesPrincipal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full roles catalog (with policies) — drives grouping and the live preview. */
  roles: readonly AccessRole[];
  /** Role ids currently assigned to the principal. */
  assignedRoleIds: readonly string[];
  /** When false the drawer is read-only (view, not edit). */
  canManage: boolean;
  /** Persist an added role (parent calls the API and updates its state). */
  onAssign: (roleId: string) => Promise<unknown>;
  /** Persist a removed role. */
  onRemove: (roleId: string) => Promise<unknown>;
}

/**
 * Shared right-side drawer to assign roles to a principal (person now, API key later). Auto-saves: each
 * toggle persists immediately and surfaces the "changes saved automatically" cue. The live effective matrix
 * and the deny notice recompute from the current selection. Read-only when `canManage` is false.
 */
export function AssignRolesDrawer({
  principal,
  open,
  onOpenChange,
  roles,
  assignedRoleIds,
  canManage,
  onAssign,
  onRemove,
}: AssignRolesDrawerProps) {
  const { t } = useTranslations("settings");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(assignedRoleIds));
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [seededId, setSeededId] = useState<string | null>(null);
  const [roleQuery, setRoleQuery] = useState("");
  /**
   * Which groups are expanded. Seeded per principal from the roles they already hold, then owned entirely by
   * the user: a group is open because it was seeded, clicked open, or worked in — never because of a count
   * that moves while they edit.
   */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  /** The role whose save is in flight, so its own row can show the wait instead of a global lock. */
  const [pendingId, setPendingId] = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-seed the local selection on a different principal AND on every open. Keying on `open` (closed → null)
  // mirrors the editor drawers: closing resets `seededId`, so reopening the SAME principal re-reads the
  // authoritative `assignedRoleIds` instead of stale local state (e.g. roles changed in the background, or a
  // previous save left `status="error"`). While open for the same principal `currentId === seededId`, so
  // local state stays authoritative and optimistic toggles aren't clobbered by parent re-renders.
  const currentId = open ? (principal?.id ?? null) : null;
  if (currentId !== seededId) {
    setSeededId(currentId);
    if (open) {
      setSelected(new Set(assignedRoleIds));
      setStatus("idle");
      // Expanded groups and the role filter both describe THIS principal, so they are re-seeded alongside
      // the selection. Carried over, a panel collapsed for one person would hide roles another one holds,
      // and the drawer would open already filtered by a term typed for someone else.
      setOpenGroups(groupsHoldingRoles(assignedRoleIds, roles));
      setRoleQuery("");
    }
  }

  // Which principal the drawer is currently showing, readable from an async continuation. Written in an
  // effect rather than during render so the ref lint rule stays happy.
  const sessionRef = useRef<string | null>(null);
  useEffect(() => {
    sessionRef.current = currentId;
  }, [currentId]);

  useEffect(() => {
    if (status !== "saved") return;
    savedTimer.current = setTimeout(() => setStatus("idle"), 2500);
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [status]);

  /**
   * Auto-save toggle. One save at a time, but the lock is now VISIBLE rather than silent.
   *
   * It used to `return` on a second click while a save was in flight, dropping it with no feedback. Grouped
   * into accordions the user ticks several roles in a row, so they would believe they had assigned five when
   * only one landed. Tracking WHICH role is saving lets every row render as inert and that row show the
   * spinner, so a click that cannot land also cannot look like it did.
   */
  const toggle = async (roleId: string, groupId: string) => {
    if (!canManage || pendingId !== null) return;
    // Ticking inside a group is the clearest statement that the user is working there — and while a search
    // term is active it is the ONLY one they can make, since header clicks are ignored. Recording it keeps
    // the group open once the term is cleared.
    setOpenGroups((prev) => (prev[groupId] ? prev : { ...prev, [groupId]: true }));
    setPendingId(roleId);
    // Whose save this is. The drawer keeps its state while closed — it renders `null`, it does not unmount —
    // so a save can still be in the air when it reopens on someone else, and the failure path below writes
    // a whole role set back into the form.
    const owner = currentId;
    const isOn = selected.has(roleId);
    const next = new Set(selected);
    if (isOn) next.delete(roleId);
    else next.add(roleId);
    setSelected(next);
    setStatus("saving");
    try {
      if (isOn) await onRemove(roleId);
      else await onAssign(roleId);
      if (sessionRef.current === owner) setStatus("saved");
    } catch (error) {
      if (sessionRef.current === owner) {
        setSelected(selected); // revert optimistic change
        setStatus("error");
      }
      // The failure is real whoever is on screen now — the operator has to hear about it either way.
      toastApiError(error);
    } finally {
      // Unconditional: it belongs to the save that just finished, and it is what unblocks the drawer. While
      // it was non-null no other toggle could start, so it cannot clobber anyone else's.
      setPendingId(null);
    }
  };

  const selectedRoles = useMemo(
    () => [...selected].map((id) => roles.find((r) => r.id === id)).filter((r): r is AccessRole => !!r),
    [selected, roles],
  );
  const statements = useMemo(() => gatherStatements([...selected], roles), [selected, roles]);
  const denyPolicies = useMemo(() => {
    const names = new Set<string>();
    for (const role of selectedRoles) {
      for (const policy of role.policies) {
        if (policy.effect === "deny") names.add(policy.name);
      }
    }
    return [...names];
  }, [selectedRoles]);

  // Search over what an operator can actually read on the row, plus the slug — `cards_read` is what a
  // developer looking at an API response would paste in.
  const term = roleQuery.trim().toLowerCase();
  const matching = useMemo(() => {
    if (!term) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(term) ||
        role.slug.toLowerCase().includes(term) ||
        (role.description?.toLowerCase().includes(term) ?? false),
    );
  }, [roles, term]);

  /**
   * Roles bucketed into their accordion groups, in render order: the platform-wide roles first (they are what
   * an admin reaches for most), then the organization's own, then one group per catalog module in the
   * catalog's own order, and finally the two honest leftovers.
   *
   * The module is derived from each role's statements, so the grouping states something true about what the
   * role grants rather than decorating the list.
   */
  const buckets = useMemo(() => {
    const byId = new Map<string, RoleBucket>();
    const ensure = (id: string, label: string, icon?: LucideIcon): RoleBucket => {
      const existing = byId.get(id);
      if (existing) return existing;
      const created: RoleBucket = { id, label, icon, roles: [] };
      byId.set(id, created);
      return created;
    };

    for (const role of matching) {
      const group = resolveRoleGroup(role);
      const id = roleGroupId(group);
      let view: RoleBucket;
      if (group.kind === "module") {
        const mod = ACCESS_CATALOG.find((m) => m.id === group.moduleId);
        view = ensure(id, mod ? t(mod.labelKey) : group.moduleId, mod?.icon);
      } else if (group.kind === "broad") {
        view = ensure(id, t("access_drawer_group_broad"));
      } else if (group.kind === "org") {
        view = ensure(id, t("access_drawer_group_org"));
      } else if (group.kind === "empty") {
        view = ensure(id, t("access_drawer_group_empty"));
      } else {
        view = ensure(id, t("access_drawer_group_unknown"));
      }
      view.roles.push(role);
    }

    const order = (id: string): number => {
      if (id === "broad") return 0;
      if (id === "org") return 1;
      if (id === "empty") return 900;
      if (id === "unknown") return 901;
      const moduleId = id.slice("module:".length);
      const index = ACCESS_CATALOG.findIndex((m) => m.id === moduleId);
      return 10 + (index === -1 ? ACCESS_CATALOG.length : index);
    };
    return [...byId.values()].sort((a, b) => order(a.id) - order(b.id));
  }, [matching, t]);

  /**
   * The per-group tally, layered on top of the bucketing. Kept in its own memo so ticking a box re-counts
   * instead of re-deriving every role's group from its statements — and so the group list is no longer a
   * function of the selection, which is what let a panel move under the user in the first place.
   */
  const groups = useMemo<RoleGroupView[]>(
    () =>
      buckets.map((bucket) => ({
        ...bucket,
        selectedCount: bucket.roles.reduce((n, role) => n + (selected.has(role.id) ? 1 : 0), 0),
      })),
    [buckets, selected],
  );

  if (!principal) return null;

  const title = (
    <span className="flex items-center gap-2.5">
      <PrincipalAvatar name={principal.name} kind={principal.kind} size={30} />
      <span className="flex items-center gap-2">
        {principal.name}
        {principal.kind === "key" && (
          <ColocaBadge variant="secondary">{t("access_badge_api_key")}</ColocaBadge>
        )}
      </span>
    </span>
  );

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={title}
      description={principal.subtitle}
      footer={
        <div className="flex w-full items-center gap-2">
          <span className="text-[11.5px] text-muted-foreground">
            {canManage ? t("access_drawer_apply_immediately") : t("access_drawer_readonly")}
          </span>
          <AccessSavedIndicator status={status} className="ml-1" />
          <ColocaButton variant="default" size="sm" className="ml-auto" onClick={() => onOpenChange(false)}>
            {t("access_drawer_close")}
          </ColocaButton>
        </div>
      }
    >
      <div className="mb-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-foreground">
        {t("access_drawer_roles_count", { count: selected.size })}
      </div>

      <div className="mb-2.5">
        <ColocaSearchInput
          value={roleQuery}
          onValueChange={setRoleQuery}
          placeholder={t("access_drawer_search_roles_placeholder")}
        />
      </div>

      {groups.length === 0 ? (
        <div className="px-0.5 py-3 text-[12px] text-muted-foreground">
          {t("access_drawer_no_role_matches")}
        </div>
      ) : (
        <ColocaAccordion>
          {groups.map((group) => (
            <ColocaAccordionItem
              key={group.id}
              title={group.label}
              icon={group.icon}
              // While searching every surviving group is expanded: the panel unmounts when closed, so a
              // match hidden inside a collapsed group would read as "no results".
              open={term ? true : (openGroups[group.id] ?? false)}
              // While a search term forces every group open, a header click cannot do anything — so it
              // must not be recorded either, or clearing the term would collapse a group the user only
              // "closed" because the control looked live.
              onOpenChange={(next) => {
                if (!term) setOpenGroups((prev) => ({ ...prev, [group.id]: next }));
              }}
              // Hidden while searching: both halves would be computed over the FILTERED roles, so a
              // principal holding 3 of a module's 6 roles reads "1/1" — which says the opposite. The
              // tally cannot be stated truthfully without the whole bucket, so it is not stated.
              meta={
                term ? undefined : (
                  <span
                    className={cn(
                      "font-mono text-[10.5px] tabular-nums",
                      group.selectedCount > 0 ? "text-primary" : "text-muted-foreground/70",
                    )}
                  >
                    {group.selectedCount}/{group.roles.length}
                  </span>
                )
              }
            >
              <RoleAssignRows
                groupId={group.id}
                roles={group.roles}
                selected={selected}
                readOnly={!canManage}
                pendingId={pendingId}
                onToggle={toggle}
              />
            </ColocaAccordionItem>
          ))}
        </ColocaAccordion>
      )}

      {denyPolicies.length > 0 && (
        <div className="mt-4 flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-[12px] text-foreground">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-destructive" />
          <span>{t("access_drawer_deny_notice", { count: denyPolicies.length })}</span>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-md border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3.5 py-2.5">
          <Shield size={13} className="text-primary" />
          <span className="text-[12.5px] font-semibold text-foreground">
            {t("access_drawer_effective_title")}
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {t("access_drawer_effective_hint")}
          </span>
        </div>
        <div className="p-2.5">
          {selected.size === 0 ? (
            <div className="px-2 py-4 text-center text-[12.5px] text-muted-foreground">
              {t("access_drawer_no_roles_empty")}
            </div>
          ) : (
            <EffectiveMatrix statements={statements} />
          )}
        </div>
      </div>
    </ColocaDrawer>
  );
}

interface RoleAssignRowsProps {
  /** Reported back on toggle so the drawer keeps the group the user is working in expanded. */
  groupId: string;
  roles: readonly AccessRole[];
  selected: Set<string>;
  readOnly: boolean;
  /** The role whose save is in flight; every row is inert while one is, but only that row shows it. */
  pendingId: string | null;
  onToggle: (roleId: string, groupId: string) => void;
}

function RoleAssignRows({ groupId, roles, selected, readOnly, pendingId, onToggle }: RoleAssignRowsProps) {
  const { t } = useTranslations("settings");

  return (
    <div>
      {roles.length === 0 ? (
        <div className="px-0.5 py-1 text-[11.5px] text-muted-foreground/70">
          {t("access_drawer_no_roles_available")}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {roles.map((role) => {
            const on = selected.has(role.id);
            const isGlobal = !role.organization_id;
            const hasDeny = role.policies.some((p) => p.effect === "deny");
            const isPending = pendingId === role.id;
            // Visibly inert while ANY save is in flight, rather than accepting the click and dropping it.
            const isBusy = pendingId !== null;
            return (
              <button
                key={role.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={readOnly || isBusy}
                onClick={() => onToggle(role.id, groupId)}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                  on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-border/80",
                  readOnly && "cursor-default",
                  isBusy && !isPending && "opacity-50",
                )}
              >
                <span
                  className={cn(
                    "grid h-[18px] w-[18px] shrink-0 place-items-center rounded border",
                    on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card",
                  )}
                >
                  {isPending ? <Loader2 size={11} className="animate-spin" /> : on && <Check size={11} />}
                </span>
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: roleColor(role.slug) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[12.5px] font-semibold text-foreground">{role.name}</span>
                    {isGlobal && <Lock size={9} className="text-muted-foreground/70" />}
                    {hasDeny && (
                      <span className="font-mono text-[9px] font-bold text-destructive">
                        {t("access_policy_effect_deny")}
                      </span>
                    )}
                  </span>
                  {role.description && (
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {role.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
